import ThumbnailWithFallback from '~/components/photos/thumbnail-with-fallback';
import { Badge } from '~/components/ui/badge';
import { Icon } from '~/components/ui/icon';
import {
  type ExternalPresenterDetails,
  type PresenterDetails,
} from '~/domain/events/models/presenter-details';
import { THUMBNAIL_SIZE } from '~/domain/shared/models/thumbnail-size';
import { cn } from '~/styles/extended-theme';

import { Section } from './section';

import './presenter-information.css';

interface PresenterInformationProps {
  className?: string;
  presenter: PresenterDetails | ExternalPresenterDetails | null;
}

export function PresenterInformation({ className, presenter }: PresenterInformationProps) {
  if (!presenter) return null;

  // Handle external presenters differently (simplified display)
  if (presenter.isExternal) {
    return (
      <div className={cn('flex-1', className)}>
        <Section title="Presenter">
          <div className="flex flex-row gap-4">
            <ThumbnailWithFallback
              imageClassName="size-auto max-w-20 max-h-20"
              size={THUMBNAIL_SIZE.SQUARE_EXTRA_SMALL}
              url={presenter.thumbnailUrl ?? '/img/person_230x230.png'}
              rounded
            />
            <div>
              <div className="break-all">{presenter.name}</div>
            </div>
          </div>
        </Section>
        {presenter.about ? (
          <Section title="About" className="text-body2">
            {presenter.about}
          </Section>
        ) : null}
      </div>
    );
  }

  // Original athlete presenter display
  return (
    <div className={cn('flex-1', className)}>
      <Section title="Presenter">
        <div className="flex flex-row gap-4">
          <ThumbnailWithFallback
            imageClassName="size-auto max-w-20 max-h-20"
            size={THUMBNAIL_SIZE.SQUARE_EXTRA_SMALL}
            url={presenter.thumbnailUrl}
            rounded
          />
          <div>
            {presenter.titles.length > 0 ? (
              <div className="text-wrap text-caption text-secondary">
                {presenter.titles.map((title) => title.title).join(' | ')}
              </div>
            ) : null}
            <div className="break-all">{presenter.name}</div>
            {presenter.expertises.length > 0 ? (
              <div className="mt-1 flex flex-row flex-wrap gap-1">
                {presenter.expertises.map((expertise) => (
                  <Badge key={expertise.id} text={expertise.expertise} />
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {presenter.belt ? (
          <div className="mt-4 flex flex-row items-center gap-2 text-body1">
            <Icon name="gi" />
            {[presenter.belt, presenter.degree].filter((x) => x).join(' - ')}
          </div>
        ) : null}
      </Section>
      {presenter.about ? (
        <Section title="About" className="text-body2">
          {presenter.about}
        </Section>
      ) : null}
      {presenter.teacherAndGym ? (
        <Section title="Lineage" className="text-body2">
          {presenter.teacherAndGym}
        </Section>
      ) : null}
      {presenter.facebookUrl || presenter.instagramUrl ? (
        <Section title="Connect" className="flex flex-row gap-3">
          {presenter.facebookUrl ? (
            <a href={presenter.facebookUrl} target="_blank" rel="noreferrer">
              <Icon name="facebook" size="xl" />
            </a>
          ) : null}

          {presenter.instagramUrl ? (
            <a href={presenter.instagramUrl} target="_blank" rel="noreferrer">
              <Icon name="instagram" size="xl" />
            </a>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}
